import { Injectable, inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APP_BUILD_VERSION } from '../../environments/build-version';

const VERSION_URL = '/assets/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_MS = 15_000;
const RELOAD_KEY = 'ic-build-reload';
/** Query param that forces the browser to refetch the HTML instead of using its cache. */
const CACHE_BUST_PARAM = '_v';

/** Local `ng serve` stays on the placeholder version and must not reload itself. */
function versionChecksEnabled(): boolean {
  return APP_BUILD_VERSION !== 'dev';
}

function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError')
  );
}

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly snack = inject(MatSnackBar);

  private pollTimer: ReturnType<typeof setInterval> | undefined;
  private updatePromptShown = false;
  private reloading = false;

  start(): void {
    if (!versionChecksEnabled()) {
      return;
    }

    void this.checkForUpdate();
    this.pollTimer = setInterval(() => void this.checkForUpdate(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** True when hosting has a newer build than the one running in this tab. */
  async isStale(): Promise<boolean> {
    if (!versionChecksEnabled()) {
      return false;
    }
    const latestVersion = await this.fetchLatestVersion();
    return !!latestVersion && latestVersion !== APP_BUILD_VERSION;
  }

  /**
   * Hard-reloads the current hosting build at `url`. Automatic attempts inside the
   * guard window are skipped so a broken chunk cannot reload forever; `force` is for
   * an explicit user click. Returns false when the reload was skipped.
   */
  reload(url: string, options: { force?: boolean } = {}): boolean {
    if (this.reloading) {
      return true;
    }
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (!options.force && Date.now() - last < RELOAD_GUARD_MS) {
      return false;
    }
    this.reloading = true;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    void hardReload(url);
    return true;
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.checkForUpdate();
    }
  };

  private async checkForUpdate(): Promise<void> {
    if (this.updatePromptShown) {
      return;
    }

    const latestVersion = await this.fetchLatestVersion();
    if (latestVersion && latestVersion !== APP_BUILD_VERSION) {
      this.showUpdatePrompt();
    }
  }

  private async fetchLatestVersion(): Promise<string | null> {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }
      const payload = (await response.json()) as { version?: string };
      return typeof payload.version === 'string' ? payload.version : null;
    } catch {
      return null;
    }
  }

  private showUpdatePrompt(): void {
    this.updatePromptShown = true;
    const ref = this.snack.open('Hay una nueva versión disponible.', 'Recargar', {
      duration: 0,
    });
    ref.onAction().subscribe(() =>
      this.reload(location.pathname + location.search + location.hash, { force: true })
    );
  }
}

async function hardReload(url: string): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Not fatal: the cache-busting URL below still refetches the HTML.
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Same as above.
  }
  await refreshEntryFiles();
  const path = url.startsWith('/') ? url : `/${url}`;
  const target = new URL(path, location.origin);
  target.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
  location.replace(target.href);
}

/**
 * Re-downloads index.html and the scripts and styles it loads, bypassing the HTTP cache.
 * JS and CSS are served as immutable for a year, so a file whose name did not change
 * between builds would otherwise come from the stale cache after reload.
 */
async function refreshEntryFiles(): Promise<void> {
  try {
    const response = await fetch('/index.html', { cache: 'reload' });
    if (!response.ok) {
      return;
    }
    const html = await response.text();
    const urls = new Set<string>();
    for (const match of html.matchAll(/(?:src|href)="([^"]+\.(?:js|css))"/g)) {
      const asset = new URL(match[1], location.origin);
      if (asset.origin === location.origin) {
        urls.add(asset.href);
      }
    }
    await Promise.all([...urls].map((asset) => fetch(asset, { cache: 'reload' }).catch(() => undefined)));
  } catch {
    // The cache-busting navigation below still runs.
  }
}

/** Removes the hard-reload query param before the router reads the URL. */
export function stripCacheBustParam(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) {
    return;
  }
  url.searchParams.delete(CACHE_BUST_PARAM);
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}

/** Runs before a lazy page is fetched, so a tab open across a deploy never asks for a deleted chunk. */
export const freshBuildGuard: CanActivateFn = async (_route, state) => {
  const versions = inject(AppVersionService);
  if (!(await versions.isStale())) {
    return true;
  }
  // If the reload was skipped, try the old chunks rather than leave a blank page.
  return !versions.reload(state.url);
};

export { isStaleChunkError };
